/**

 * ═══════════════════════════════════════════════════════════════════════════

 * PLAN SERVICE - Business Plan Management & Access Control

 * ═══════════════════════════════════════════════════════════════════════════

 * 

 * This service manages all plan-related logic including:

 * - Free trial detection and management

 * - Plan limits enforcement (products, domains, features)

 * - Internal team whitelist bypass

 * - Feature access control

 * 

 * @author MyTamilKart Development Team

 * @version 2.0

 * @lastModified 2025-12-18

 */

import api from '../api/axiosInstance';

import { INTERNAL_TEAM_WHITELIST } from '../config/internalTeam';

// ═══════════════════════════════════════════════════════════════════════════

// SECTION 1: CONFIGURATION

// ═══════════════════════════════════════════════════════════════════════════

/**

 * FREE TRIAL CONFIGURATION

 * -------------------------

 * Change this value to adjust free trial period for ALL users.

 * 

 * @constant {number} FREE_TRIAL_DAYS - Number of days for free trial

 * @default 14

 * 

 * EXAMPLES:

 * - Set to 14: Users get 14 days free trial

 * - Set to 30: Users get 30 days free trial

 * - Set to 7: Users get 7 days free trial

 * 

 * IMPORTANT:

 * - Changing this value affects ALL users (existing + new)

 * - Users with 4 days left on 14-day trial will get extended if you increase it

 * - Expired trials may be reactivated if you increase the value

 * 

 * HOW IT WORKS:

 * Trial active if: (Today - created_date) <= FREE_TRIAL_DAYS

 */

const FREE_TRIAL_DAYS = 14;

/**

 * PLAN HIERARCHY CONFIGURATION

 * -----------------------------

 * Defines the tier level of each plan for upgrade/downgrade detection.

 * Higher number = higher tier.

 * 

 * @constant {Object} PLAN_HIERARCHY - Plan tier levels

 * 

 * USAGE:

 * - Used to determine if a plan change is an upgrade or downgrade

 * - Used to block downgrades in the UI

 * - Used in purchase validation

 * 

 * EXAMPLES:

 * - Starter (1) → Pro (2) = Upgrade ✅

 * - Premium (3) → Starter (1) = Downgrade ❌ (blocked)

 * - Pro (2) → Pro (2) = Renewal 🔄

 */

export const PLAN_HIERARCHY = {

  'Starter': 1,

  'pro': 2,

  'premium': 3

};

/**

 * PLAN LIMITS CONFIGURATION (FALLBACK)

 * -------------------------------------

 * These are FALLBACK limits used when API is unavailable.

 * 

 * ⚠️ IMPORTANT: 

 * - Primary source: API (/plans/list)

 * - Fallback source: These hardcoded values

 * - To change limits: Update in Super Admin Panel (preferred)

 * - These values are used only if API fails

 * 

 * @constant {Object} FALLBACK_PLAN_LIMITS - Fallback plan limits

 */

const FALLBACK_PLAN_LIMITS = {

  'Starter': {

    maxProducts: 50,

    maxDomains: 1,

    analytics: 'basic',

    discountCodes: false,

    multiStore: false,

    support: 'email',

  },

  'pro': {

    maxProducts: 150,

    maxDomains: 1,

    analytics: 'advanced',

    discountCodes: true,

    multiStore: false,

    support: 'priority',

  },

  'premium': {

    maxProducts: 500,

    maxDomains: -1, // unlimited

    analytics: 'premium',

    discountCodes: true,

    multiStore: true,

    support: '24/7',

  },

  'basic': {

    maxProducts: 0,

    maxDomains: 0,

    analytics: 'none',

    discountCodes: false,

    multiStore: false,

    support: 'none',

    readOnly: true,

  }

};

/**

 * CACHED PLANS FROM API

 * ----------------------

 * Plans fetched from /plans/list API are cached here.

 * This cache is updated when fetchPlansFromAPI() is called.

 * 

 * @type {Array|null} cachedPlans - Cached plan data from API

 */

let cachedPlans = null;

/**
 * In-flight /plans/list request, shared so concurrent callers await the same fetch
 * (App init and PlanContext both request plans on mount).
 */
let plansFetchPromise = null;

// ═══════════════════════════════════════════════════════════════════════════

// SECTION 2: HELPER FUNCTIONS (Internal Use)

// ═══════════════════════════════════════════════════════════════════════════

/**

 * Parse plan features from API response

 * --------------------------------------

 * Converts features array from API into structured limits object.

 * 

 * API format: ["maxProducts: 50", "maxDomains: 1", "analytics: 'basic'"]

 * Output format: { maxProducts: 50, maxDomains: 1, analytics: 'basic' }

 * 

 * @param {Array} features - Features array from API

 * @returns {Object} Parsed limits object

 */

function parsePlanFeatures(features) {

  const limits = {};

  

  if (!Array.isArray(features)) {

    console.warn('[PLAN SERVICE] ⚠️ Features is not an array:', features);

    return limits;

  }

  

  console.log('[PLAN SERVICE] 🔍 Parsing features:', features);

  

  features.forEach(feature => {

    try {

      // Skip empty strings

      if (!feature || feature.trim() === '') {

        return;

      }

      

      // Parse "maxProducts: 50" or "analytics: 'premium'" format

      const match = feature.match(/^(\w+):\s*(.+)$/);

      if (match) {

        const [, key, value] = match;

        

        // Remove ALL quotes (single and double) and trim

        let parsedValue = value.trim().replace(/^['"]|['"]$/g, '').replace(/'/g, '');

        

        // Convert to appropriate type

        if (parsedValue === 'true') {

          parsedValue = true;

        } else if (parsedValue === 'false') {

          parsedValue = false;

        } else if (!isNaN(parsedValue) && parsedValue !== '') {

          parsedValue = Number(parsedValue);

        }

        // else keep as string (for 'basic', 'advanced', 'premium', 'email', etc.)

        

        limits[key] = parsedValue;

        console.log(`[PLAN SERVICE] ✅ Parsed: ${key} = ${JSON.stringify(parsedValue)} (type: ${typeof parsedValue})`);

      } else {

        console.warn(`[PLAN SERVICE] ⚠️ Could not parse feature: "${feature}"`);

      }

    } catch (error) {

      console.warn('[PLAN SERVICE] ❌ Failed to parse feature:', feature, error);

    }

  });

  

  console.log('[PLAN SERVICE] 📊 Final parsed limits:', limits);

  return limits;

}

/**
 * Resolve fallback limits from a plan name
 * ------------------------------------------
 * Paid plans are identified by Mongo ObjectIds, which never match the
 * FALLBACK_PLAN_LIMITS keys, so match on the plan name instead
 * (e.g. "Pro Annual" → pro). Defaults to Starter, never to basic.
 *
 * @param {string} planName - Plan display name
 * @returns {Object} Fallback limits for a paid plan
 */
function getFallbackLimitsByName(planName) {
  const name = (planName || '').toLowerCase();
  if (name.includes('premium')) return FALLBACK_PLAN_LIMITS.premium;
  // "pro" as a word: "Yearly Pro Plan", not "Promo Starter"
  if (/\bpro\b/.test(name)) return FALLBACK_PLAN_LIMITS.pro;
  return FALLBACK_PLAN_LIMITS.Starter;
}

const planService = {

  /**

   * Fetch plans from API

   * ---------------------

   * Fetches active plans from /plans/list API and caches them.

   * This should be called on app initialization.

   * 

   * @returns {Promise<Array>} Array of plans from API

   * 

   * @example

   * await planService.fetchPlansFromAPI();

   * // Plans are now cached and ready to use

   */

  fetchPlansFromAPI() {
    if (!plansFetchPromise) {
      plansFetchPromise = this._fetchPlansFromAPI().finally(() => {
        plansFetchPromise = null;
      });
    }
    return plansFetchPromise;
  },

  /**
   * Ensure plans are cached before resolving limits
   * ------------------------------------------------
   * Waits for (or starts) the /plans/list fetch if the cache is empty.
   *
   * @returns {Promise<Array|null>} Cached plans
   */
  async ensurePlansLoaded() {
    if (!cachedPlans) {
      await this.fetchPlansFromAPI();
    }
    return cachedPlans;
  },

  async _fetchPlansFromAPI() {

    try {

      console.log('[PLAN SERVICE] 🔄 Fetching plans from API...');

      

      const response = await api.get('/plans/list');

      

      if (response.data.status === 'success' && response.data.result?.plans) {

        cachedPlans = response.data.result.plans;

        

        console.log('[PLAN SERVICE] ✅ Plans fetched successfully:', {

          count: cachedPlans.length,

          plans: cachedPlans.map(p => p.plan_id)

        });

        

        return cachedPlans;

      } else {

        throw new Error('Invalid API response');

      }

    } catch (error) {

      console.error('[PLAN SERVICE] ❌ Failed to fetch plans from API:', error);

      console.warn('[PLAN SERVICE] ⚠️ Using fallback hardcoded limits');

      cachedPlans = null;

      return [];

    }

  },

  /**

   * Get cached plans

   * ----------------

   * Returns the cached plans from API.

   * 

   * @returns {Array|null} Cached plans or null if not fetched

   */

  getCachedPlans() {

    return cachedPlans;

  },

  /**

   * Check if user is internal team member

   * ----------------------------------------

   * Internal team members bypass all plan restrictions.

   * 

   * @param {string} businessUserId - Business user ID to check

   * @returns {boolean} True if user is in internal team whitelist

   * 

   * @example

   * isInternalTeam('68be76563bbf9d4a58eb1f8c') // true if in whitelist

   */

  isInternalTeam(businessUserId) {

    return INTERNAL_TEAM_WHITELIST.includes(businessUserId);

  },

  /**

   * Check if user is in free trial period

   * --------------------------------------

   * Determines if user is within the free trial period based on created_date.

   * 

   * @param {Object} planInfo - Plan information object

   * @param {string} [createdDate=null] - Optional created_date if not in planInfo

   * @returns {boolean} True if user is in active free trial

   * 

   * LOGIC:

   * 1. Check if created_date exists in planInfo

   * 2. Calculate days since creation

   * 3. Compare with FREE_TRIAL_DAYS constant

   * 4. Return true if within trial period

   * 

   * @example

   * // User created 10 days ago, FREE_TRIAL_DAYS = 14

   * isInFreeTrial(planInfo) // true (10 <= 14)

   * 

   * // User created 20 days ago, FREE_TRIAL_DAYS = 14

   * isInFreeTrial(planInfo) // false (20 > 14)

   */

  isInFreeTrial(planInfo, createdDate = null) {

    console.log('[PLAN SERVICE] Checking free trial status...', { 

      plan_status: planInfo.plan_status,

      has_created_date: !!(planInfo.created_date || planInfo.store_created_date || createdDate),

      trial_days: FREE_TRIAL_DAYS

    });

    // Check if store_created_date exists in planInfo

    if (planInfo.store_created_date || planInfo.created_date) {

      const storeCreatedDate = new Date(planInfo.store_created_date || planInfo.created_date);

      const today = new Date();

      const daysSinceCreation = Math.floor((today - storeCreatedDate) / (1000 * 60 * 60 * 24));

      

      console.log(`[PLAN SERVICE] Days since creation: ${daysSinceCreation} (Trial period: ${FREE_TRIAL_DAYS} days)`);

      

      if (daysSinceCreation <= FREE_TRIAL_DAYS) {

        console.log(`[PLAN SERVICE] ✅ Free trial active (within ${FREE_TRIAL_DAYS} days)`);

        return true;

      } else {

        console.log(`[PLAN SERVICE] ❌ Free trial expired (> ${FREE_TRIAL_DAYS} days)`);

        return false;

      }

    }

    // Check if createdDate was passed as parameter

    if (createdDate) {

      const storeCreatedDate = new Date(createdDate);

      const today = new Date();

      const daysSinceCreation = Math.floor((today - storeCreatedDate) / (1000 * 60 * 60 * 24));

      

      console.log(`[PLAN SERVICE] Days since creation (from param): ${daysSinceCreation} (Trial period: ${FREE_TRIAL_DAYS} days)`);

      

      if (daysSinceCreation <= FREE_TRIAL_DAYS) {

        console.log(`[PLAN SERVICE] ✅ Free trial active (within ${FREE_TRIAL_DAYS} days)`);

        return true;

      } else {

        console.log(`[PLAN SERVICE] ❌ Free trial expired (> ${FREE_TRIAL_DAYS} days)`);

        return false;

      }

    }

    // Fallback: Check trial_expires_on field

    if (planInfo.trial_expires_on) {

      const trialExpiry = new Date(planInfo.trial_expires_on);

      const today = new Date();

      const isActive = trialExpiry > today;

      console.log('[PLAN SERVICE]', isActive ? '✅' : '❌', 'Trial via trial_expires_on:', trialExpiry);

      return isActive;

    }

    console.log('[PLAN SERVICE] ❌ No free trial detected');

    return false;

  },

  /**

   * Fetch business user created_date

   * ----------------------------------

   * Retrieves the created_date from business APIs.

   * Tries multiple endpoints for reliability.

   * 

   * @param {string} businessUserId - Business user ID

   * @returns {Promise<string|null>} Created date or null if not found

   * 

   * API ENDPOINTS TRIED:

   * 1. /business/store/info (primary)

   * 2. /admin/business/list (fallback)

   */

  async getBusinessCreatedDate(businessUserId) {

    try {

      // Try business store info API first

      const storeResponse = await api.get(`/business/store/info?business_id=${businessUserId}`);

      

      if (storeResponse.data.status === 'success' && storeResponse.data.result) {

        const createdDate = storeResponse.data.result.created_date;

        if (createdDate) {

          console.log('[PLAN SERVICE] ✅ Found created_date from store info:', createdDate);

          return createdDate;

        }

      }

      

      // Fallback: Try business list API

      console.log('[PLAN SERVICE] Trying business list API...');

      const listResponse = await api.get(`/admin/business/list?limit=1&search_text=${businessUserId}`);

      

      if (listResponse.data.status === 'success' && listResponse.data.result.users?.length > 0) {

        const user = listResponse.data.result.users[0];

        if (user.created_date) {

          console.log('[PLAN SERVICE] ✅ Found created_date from business list:', user.created_date);

          return user.created_date;

        }

      }

      

      console.log('[PLAN SERVICE] ❌ No created_date found in any API');

      return null;

    } catch (error) {

      console.error('[PLAN SERVICE] ❌ Error fetching business created date:', error);

      return null;

    }

  },

  /**

   * Internal helper to check feature availability

   * ----------------------------------------------

   * Checks if a specific feature is available in the given limits.

   * 

   * @param {string} feature - Feature name to check

   * @param {Object} limits - Plan limits object

   * @returns {boolean} True if feature is available

   * 

   * @private

   */

  _checkFeatureInLimits(feature, limits) {

    switch (feature) {

      case 'addProduct':

        return !limits.readOnly;

      case 'editProduct':

        return !limits.readOnly;

      case 'deleteProduct':

        return !limits.readOnly;

      case 'discountCodes':

        return limits.discountCodes;

      case 'advancedAnalytics':

        return limits.analytics === 'advanced' || limits.analytics === 'premium';

      case 'premiumAnalytics':

        return limits.analytics === 'premium';

      case 'multiStore':

        return limits.multiStore;

      case 'unlimitedDomains':

        return limits.maxDomains === -1;

      default:

        return false;

    }

  },

  // ═══════════════════════════════════════════════════════════════════════════

  // SECTION 3: MAIN API FUNCTIONS (Public Interface)

  // ═══════════════════════════════════════════════════════════════════════════

  /**

   * Get plan information for a business user

   * -----------------------------------------

   * Main function to fetch and process plan information.

   * 

   * @param {string} businessUserId - Business user ID

   * @returns {Promise<Object>} Plan information object

   * 

   * PRIORITY ORDER:

   * 1. Internal Team → Full Premium access

   * 2. Active Paid Plan → Plan-specific access

   * 3. Free Trial → Starter plan access

   * 4. No Plan/Expired → Read-only mode

   * 

   * RETURNED OBJECT:

   * {

   *   plan_id: string,

   *   plan_name: string,

   *   is_expired: boolean,

   *   plan_status: string,

   *   isReadOnly: boolean,

   *   isFreeTrial: boolean,

   *   isInternalTeam: boolean,

   *   limits: Object,

   *   hasActivePlan: boolean,

   *   trialDaysRemaining: number (if in trial)

   * }

   * 

   * @example

   * const planInfo = await getPlanInfo('user123');

   * if (planInfo.isFreeTrial) {

   *   console.log(`Trial expires in ${planInfo.trialDaysRemaining} days`);

   * }

   */

  async getPlanInfo(businessUserId) {

    try {

      // PRIORITY 1: Check if user is internal team

      if (this.isInternalTeam(businessUserId)) {

        console.log('[PLAN SERVICE] Internal team member detected - full access granted');

        return {

          plan_id: 'premium',

          plan_name: 'Internal Team',

          is_expired: false,

          plan_status: 'internal',

          isReadOnly: false,

          isInternalTeam: true,

          limits: FALLBACK_PLAN_LIMITS.premium,

          hasActivePlan: true

        };

      }

      // Fetch plan info from API

      const response = await api.get(`/business/plan/info?business_user_id=${businessUserId}`);

      

      if (response.data.status === 'success') {

        const planInfo = response.data.result;

        

        // PRIORITY 2: Check if user has an ACTIVE PAID PLAN

        // Exclude "created" status and "Unknown Plan" as these are not active plans

        if (planInfo.plan_id && 

            planInfo.plan_status !== 'none' && 

            planInfo.plan_status !== 'created' &&

            planInfo.plan_name && 

            planInfo.plan_name.toLowerCase() !== 'unknown plan' &&

            !planInfo.is_expired) {

          console.log('[PLAN SERVICE] ✅ User has active plan:', planInfo.plan_name);

          

          await this.ensurePlansLoaded();
          const limits = this.getActivePlanLimits(planInfo.plan_id, planInfo.plan_name);

          

          return {

            ...planInfo,

            isReadOnly: false,

            isFreeTrial: false,

            isInternalTeam: false,

            limits,

            hasActivePlan: true

          };

        }

        

        // PRIORITY 3: Check if user is in FREE TRIAL

        let inFreeTrial = this.isInFreeTrial(planInfo);

        

        // If not in trial, try fetching created_date from business API

        if (!inFreeTrial && (!planInfo.plan_id || planInfo.plan_status === 'none')) {

          console.log('[PLAN SERVICE] No plan found, checking for free trial...');

          const createdDate = await this.getBusinessCreatedDate(businessUserId);

          if (createdDate) {

            inFreeTrial = this.isInFreeTrial(planInfo, createdDate);

            planInfo.created_date = createdDate;

          }

        }

        

        // If in free trial, return with Starter plan features

        if (inFreeTrial) {

          console.log(`[PLAN SERVICE] ✅ User is in ${FREE_TRIAL_DAYS}-day free trial`);

          

          const createdDate = new Date(planInfo.created_date || planInfo.store_created_date);

          const today = new Date();

          const daysSinceCreation = Math.floor((today - createdDate) / (1000 * 60 * 60 * 24));

          const daysRemaining = Math.max(0, FREE_TRIAL_DAYS - daysSinceCreation);

          

          return {

            ...planInfo,

            plan_id: 'Starter',

            plan_name: 'Free Trial',

            is_expired: false,

            isReadOnly: false,

            isFreeTrial: true,

            trialDaysRemaining: daysRemaining,

            trialTotalDays: FREE_TRIAL_DAYS,

            limits: FALLBACK_PLAN_LIMITS.Starter,

            hasActivePlan: true

          };

        }

        

        // PRIORITY 4: No plan or expired - READ-ONLY MODE

        const isReadOnly = planInfo.is_expired || 

                          (planInfo.plan_status === 'none' && !inFreeTrial) || 

                          (!planInfo.plan_id && !inFreeTrial) ||

                          (planInfo.plan_name && planInfo.plan_name.toLowerCase() === 'unknown plan') ||

                          planInfo.plan_status === 'created';

        

        const limits = isReadOnly
          ? this.getPlanLimits('basic')
          : this.getActivePlanLimits(planInfo.plan_id, planInfo.plan_name);

        

        // Determine appropriate plan name for expired/no plan state

        let displayPlanName = planInfo.plan_name;

        if (isReadOnly) {

          // Check if it's an expired trial

          if (planInfo.created_date || planInfo.store_created_date) {

            const createdDate = new Date(planInfo.created_date || planInfo.store_created_date);

            const today = new Date();

            const daysSinceCreation = Math.floor((today - createdDate) / (1000 * 60 * 60 * 24));

            

            // If more than FREE_TRIAL_DAYS, it's an expired trial

            if (daysSinceCreation > FREE_TRIAL_DAYS) {

              displayPlanName = 'Trial Expired';

            }

          }

          

          // If plan_name is "Unknown Plan" or empty, show generic expired message

          if (!displayPlanName || 

              displayPlanName.toLowerCase() === 'unknown plan' || 

              displayPlanName === 'Trial Expired') {

            displayPlanName = 'Plan Expired';

          }

        }

        

        return {

          ...planInfo,

          plan_name: displayPlanName,

          isReadOnly,

          isFreeTrial: false,

          isInternalTeam: false,

          limits,

          hasActivePlan: !isReadOnly

        };

      }

      

      throw new Error('Failed to fetch plan info');

    } catch (error) {

      console.error('[PLAN SERVICE] Error fetching plan info:', error);

      // Return basic plan on error

      return {

        plan_id: 'basic',

        plan_name: 'Basic',

        is_expired: true,

        plan_status: 'none',

        isReadOnly: true,

        isFreeTrial: false,

        isInternalTeam: false,

        limits: FALLBACK_PLAN_LIMITS.basic,

        hasActivePlan: false

      };

    }

  },

  /**
   * Get limits for an active paid plan
   * -----------------------------------
   * Uses the plan's features from /plans/list (matched by id, then name),
   * layered over name-based fallback limits. Plans that were deactivated
   * are missing from /plans/list, so an active plan must never resolve to
   * the read-only basic limits (maxProducts: 0).
   *
   * @param {string} planId - Plan ID (Mongo ObjectId)
   * @param {string} planName - Plan display name
   * @returns {Object} Plan limits object
   */
  getActivePlanLimits(planId, planName) {
    const fallback = getFallbackLimitsByName(planName);
    const plans = Array.isArray(cachedPlans) ? cachedPlans : [];
    const plan = plans.find(p => String(p.plan_id) === String(planId)) ||
      plans.find(p => planName && (p.name || '').toLowerCase() === planName.toLowerCase());
    const apiLimits = plan ? parsePlanFeatures(plan.features || []) : {};
    const limits = { ...fallback, ...apiLimits, readOnly: false };

    if (!(limits.maxProducts === -1 || limits.maxProducts > 0)) {
      console.warn(`[PLAN SERVICE] ⚠️ Invalid maxProducts for active plan ${planId}, using fallback`);
      limits.maxProducts = fallback.maxProducts;
    }
    if (typeof limits.maxDomains !== 'number') {
      limits.maxDomains = fallback.maxDomains;
    }

    console.log(`[PLAN SERVICE] ✅ Active plan limits for ${planName} (${planId}):`, limits);
    return limits;
  },

  /**

   * Get plan limits for a specific plan

   * ------------------------------------

   * Returns the limits configuration for a given plan ID.

   * 

   * @param {string} planId - Plan ID (Starter, pro, premium, basic)

   * @returns {Object} Plan limits object

   * 

   * @example

   * const limits = getPlanLimits('pro');

   * console.log(limits.maxProducts); // 150 (from API or fallback)

   * 

   * PRIORITY:

   * 1. Try to get from cached API data (dynamic)

   * 2. Fall back to hardcoded limits (safe fallback)

   */

  getPlanLimits(planId) {

    console.log(`[PLAN SERVICE] 🔍 Getting limits for plan: ${planId}`);

    

    // PRIORITY 1: Try cached API data

    if (cachedPlans && Array.isArray(cachedPlans)) {

      console.log(`[PLAN SERVICE] 📦 Cached plans available:`, cachedPlans.map(p => p.plan_id));

      const plan = cachedPlans.find(p => String(p.plan_id) === String(planId));

      

      if (plan) {

        console.log(`[PLAN SERVICE] ✅ Found plan in cache:`, plan);

        // Parse features from API

        const parsedLimits = parsePlanFeatures(plan.features || []);

        

        // If we got valid limits from API, use them

        if (Object.keys(parsedLimits).length > 0) {

          console.log(`[PLAN SERVICE] ✅ Using API limits for ${planId}:`, parsedLimits);

          return parsedLimits;

        } else {

          console.warn(`[PLAN SERVICE] ⚠️ No valid limits parsed from API for ${planId}`);

        }

      } else {

        console.warn(`[PLAN SERVICE] ⚠️ Plan ${planId} not found in cached plans`);

      }

    } else {

      console.warn(`[PLAN SERVICE] ⚠️ No cached plans available`);

    }

    

    // PRIORITY 2: Fall back to hardcoded limits

    console.log(`[PLAN SERVICE] ⚠️ Using fallback limits for ${planId}`);

    return FALLBACK_PLAN_LIMITS[planId] || FALLBACK_PLAN_LIMITS.basic;

  },

  /**

   * Get plan hierarchy level

   * -------------------------

   * Returns the tier level of a plan (higher = better plan).

   * 

   * @param {string} planId - Plan ID ('Starter', 'pro', 'premium')

   * @returns {number} Plan level (1-3, or 0 if not found)

   * 

   * @example

   * getPlanLevel('Starter') // 1

   * getPlanLevel('pro')     // 2

   * getPlanLevel('premium') // 3

   */

  getPlanLevel(planId) {

    return PLAN_HIERARCHY[planId] || 0;

  },

  /**

   * Check if plan change is an upgrade

   * -----------------------------------

   * Compares two plans to determine if it's an upgrade.

   * 

   * @param {string} currentPlanId - Current plan ID

   * @param {string} newPlanId - New plan ID

   * @returns {boolean} True if new plan is higher tier

   * 

   * @example

   * isUpgrade('Starter', 'pro')     // true

   * isUpgrade('premium', 'Starter') // false

   */

  isUpgrade(currentPlanId, newPlanId) {

    const currentLevel = this.getPlanLevel(currentPlanId);

    const newLevel = this.getPlanLevel(newPlanId);

    return newLevel > currentLevel;

  },

  /**

   * Check if plan change is a downgrade

   * ------------------------------------

   * Compares two plans to determine if it's a downgrade.

   * 

   * @param {string} currentPlanId - Current plan ID

   * @param {string} newPlanId - New plan ID

   * @returns {boolean} True if new plan is lower tier

   * 

   * @example

   * isDowngrade('premium', 'Starter') // true

   * isDowngrade('Starter', 'pro')     // false

   */

  isDowngrade(currentPlanId, newPlanId) {

    const currentLevel = this.getPlanLevel(currentPlanId);

    const newLevel = this.getPlanLevel(newPlanId);

    return newLevel < currentLevel;

  },

  // ═══════════════════════════════════════════════════════════════════════════

  // SECTION 4: FEATURE ACCESS CONTROL

  // ═══════════════════════════════════════════════════════════════════════════

  /**

   * Check if a feature is available

   * --------------------------------

   * Determines if a specific feature is available in the user's plan.

   * 

   * @param {Object} planInfo - Plan information object

   * @param {string} feature - Feature name to check

   * @returns {boolean} True if feature is available

   * 

   * AVAILABLE FEATURES:

   * - addProduct, editProduct, deleteProduct

   * - discountCodes

   * - advancedAnalytics, premiumAnalytics

   * - multiStore

   * - unlimitedDomains

   * 

   * @example

   * if (hasFeature(planInfo, 'discountCodes')) {

   *   // Show discount code feature

   * }

   */

  hasFeature(planInfo, feature) {

    // Internal team has access to everything

    if (planInfo?.isInternalTeam) {

      return true;

    }

    // Free trial users get Starter plan features

    if (planInfo?.isFreeTrial) {

      const limits = FALLBACK_PLAN_LIMITS.Starter;

      return this._checkFeatureInLimits(feature, limits);

    }

    if (!planInfo || planInfo.isReadOnly) {

      return false;

    }

    const limits = planInfo.limits || this.getPlanLimits(planInfo.plan_id);

    return this._checkFeatureInLimits(feature, limits);

  },

  // ═══════════════════════════════════════════════════════════════════════════

  // SECTION 5: LIMIT ENFORCEMENT

  // ═══════════════════════════════════════════════════════════════════════════

  /**

   * Check if user can add a product

   * --------------------------------

   * Validates if user has reached their product limit.

   * 

   * @param {Object} planInfo - Plan information object

   * @param {number} currentProductCount - Current number of products

   * @returns {Object} { allowed: boolean, reason: string }

   * 

   * @example

   * const check = canAddProduct(planInfo, 45);

   * if (!check.allowed) {

   *   toast.error(check.reason);

   * }

   */

  canAddProduct(planInfo, currentProductCount) {

    // Internal team has unlimited access

    if (planInfo?.isInternalTeam) {

      return { allowed: true };

    }

    // Free trial users get Starter plan limits

    if (planInfo?.isFreeTrial) {

      const limits = FALLBACK_PLAN_LIMITS.Starter;

      if (currentProductCount >= limits.maxProducts) {

        return { 

          allowed: false, 

          reason: `Free trial limit reached (${limits.maxProducts} products). Upgrade to add more.` 

        };

      }

      return { allowed: true };

    }

    // FREE TIER: Allow 1 product for users without active plan/subscription

    // This check MUST come BEFORE isReadOnly check to allow first free product

    if (planInfo.isReadOnly) {

      // Allow first product (free tier)

      if (currentProductCount < 1) {

        return { allowed: true };

      }

      // Block if trying to add more than 1 product

      return { 

        allowed: false, 

        reason: 'Please purchase a plan to add more products.' 

      };

    }

    const limits = planInfo.limits || this.getPlanLimits(planInfo.plan_id);

    

    if (limits.maxProducts === -1) {

      return { allowed: true };

    }

    if (currentProductCount >= limits.maxProducts) {

      return { 

        allowed: false, 

        reason: `Product limit reached (${limits.maxProducts}). Upgrade to add more.` 

      };

    }

    return { allowed: true };

  },

  /**

   * Check if user can add a domain

   * -------------------------------

   * Validates if user has reached their domain limit.

   * 

   * @param {Object} planInfo - Plan information object

   * @param {number} currentDomainCount - Current number of domains

   * @returns {Object} { allowed: boolean, reason: string }

   * 

   * @example

   * const check = canAddDomain(planInfo, 1);

   * if (!check.allowed) {

   *   toast.error(check.reason);

   * }

   */

  canAddDomain(planInfo, currentDomainCount) {

    // Internal team has unlimited access

    if (planInfo?.isInternalTeam) {

      return { allowed: true };

    }

    // Free trial users get Starter plan limits

    if (planInfo?.isFreeTrial) {

      const limits = FALLBACK_PLAN_LIMITS.Starter;

      if (currentDomainCount >= limits.maxDomains) {

        return { 

          allowed: false, 

          reason: `Free trial limit reached (${limits.maxDomains} domain). Upgrade to add more.` 

        };

      }

      return { allowed: true };

    }

    if (planInfo.isReadOnly) {

      return { allowed: false, reason: 'Plan expired or inactive. Please upgrade.' };

    }

    const limits = planInfo.limits || this.getPlanLimits(planInfo.plan_id);

    

    if (limits.maxDomains === -1) {

      return { allowed: true };

    }

    if (currentDomainCount >= limits.maxDomains) {

      return { 

        allowed: false, 

        reason: `Domain limit reached (${limits.maxDomains}). Upgrade to add more.` 

      };

    }

    return { allowed: true };

  },

  // ═══════════════════════════════════════════════════════════════════════════

  // SECTION 6: UTILITY FUNCTIONS

  // ═══════════════════════════════════════════════════════════════════════════

  /**

   * Get upgrade message for a feature

   * ----------------------------------

   * Returns a user-friendly message for locked features.

   * 

   * @param {string} feature - Feature name

   * @returns {string} Upgrade message

   * 

   * @example

   * const message = getUpgradeMessage('discountCodes');

   * // Returns: "Upgrade to Pro or Premium to use discount codes"

   */

  getUpgradeMessage(feature) {

    const messages = {

      'addProduct': 'Upgrade your plan to add more products',

      'discountCodes': 'Upgrade to Pro or Premium to use discount codes',

      'advancedAnalytics': 'Upgrade to Pro or Premium for advanced analytics',

      'multiStore': 'Upgrade to Premium for multi-store management',

      'unlimitedDomains': 'Upgrade to Premium for unlimited domains'

    };

    return messages[feature] || 'Upgrade your plan to access this feature';

  }

};

// ═══════════════════════════════════════════════════════════════════════════

// EXPORTS

// ═══════════════════════════════════════════════════════════════════════════

export default planService;